import { observer } from 'mobx-react-lite'
import { capitalize } from 'lodash'
import classnames from 'classnames'
import { getUrlEndPath } from 'shared/lib'
import { EmptyCell, Icon, Switch, Typography } from 'shared/ui'
import type { IIconsVector } from 'shared/ui/Icon'
import { ChatbotBookingType } from 'entities/Chatbot'
import { useDetailsContext } from 'pages/chatbot/ui/ChatbotDetails/detailsContext'
import styles from './styles.module.scss'

const PROVIDER_LABELS: Record<string, string> = {
  calendly: 'Calendly',
  hubspot: 'HubSpot',
}

const PROVIDER_ICONS: Record<string, IIconsVector> = {
  calendly: 'calendly',
  hubspot: 'hubspot',
}

const BOOKING_TYPE_LABELS: Record<string, string> = {
  [ChatbotBookingType.TimeChat]: 'Chat to book a time',
  [ChatbotBookingType.ShareLink]: 'Share booking link',
}

export const SettingsTab = observer(() => {
  const {
    data: { settings },
  } = useDetailsContext()
  const bookingEnabled = settings?.book_appointment_enabled?.value
  const provider = settings?.booking_provider?.value
  const link = settings?.book_appointment_link
  const bookingType = settings?.book_appointment_type?.value
  const smartBookingEnabled = settings?.smart_booking_enabled?.value
  const followUpEnabled = settings?.enable_followup?.value
  const followUpWindow = settings?.followup_window?.value
  const assertiveness = settings?.followup_assertiveness?.value
  const sessionLifetime = settings?.session_lifetime?.value

  const sessionLifetimeLabel = `${sessionLifetime} ${sessionLifetime === 1 ? 'day' : 'days'}`
  const endSessionText = `If the contact doesn't reply within ${sessionLifetimeLabel}${
    followUpEnabled ? ' after the follow-up window' : ''
  }`

  return (
    <>
      <div className={styles.section}>
        <div className={styles.knowledgeBaseRow}>
          <Typography variant='body-lg-medium' ariaLabel='Booking'>
            Booking
          </Typography>
          <Switch value={bookingEnabled} ariaLabel='Booking' disabled size='middle' />
        </div>
        {bookingEnabled && (
          <div className={styles.sectionRows}>
            <div className={styles.sectionRow}>
              <Typography
                ariaLabel='SchedulingApp'
                variant='text-input-field-r'
                className={styles.label}
              >
                Scheduling app
              </Typography>
              <div className={styles.value}>
                {provider ? (
                  <div className={styles.providerValue}>
                    <Icon icon={PROVIDER_ICONS[provider]} fontSize={16} />
                    <Typography ariaLabel='SchedulingAppValue' variant='body-md-regular'>
                      {PROVIDER_LABELS[provider] ?? provider}
                    </Typography>
                  </div>
                ) : (
                  <EmptyCell />
                )}
              </div>
            </div>
            {provider && (
              <>
                <div className={styles.sectionRow}>
                  <Typography
                    ariaLabel='Link'
                    variant='text-input-field-r'
                    className={styles.label}
                  >
                    Link
                  </Typography>
                  <div className={styles.value}>
                    {link ? (
                      <div className={styles.linkValue}>
                        <Typography
                          ariaLabel='LinkName'
                          variant='text-input-field-r'
                          ellipsis
                          tag='div'
                          tooltipProps={{ label: link.name }}
                        >
                          {link.name}
                        </Typography>
                        <Typography
                          ariaLabel='LinkUrl'
                          variant='text-input-field-r'
                          className={styles.linkUrl}
                          ellipsis
                          tag='div'
                          tooltipProps={{ label: getUrlEndPath(link.value) }}
                        >
                          {getUrlEndPath(link.value)}
                        </Typography>
                      </div>
                    ) : (
                      <EmptyCell />
                    )}
                  </div>
                </div>
                <div className={styles.sectionRow}>
                  <Typography
                    ariaLabel='Method'
                    variant='text-input-field-r'
                    className={styles.label}
                  >
                    Method
                  </Typography>
                  <div className={styles.value}>
                    {bookingType ? (
                      <Typography ariaLabel='MethodValue' variant='body-md-regular'>
                        {BOOKING_TYPE_LABELS[bookingType]}
                      </Typography>
                    ) : (
                      <EmptyCell />
                    )}
                  </div>
                </div>
                <div className={styles.sectionRow}>
                  <Typography
                    ariaLabel='SmartMeeting'
                    variant='text-input-field-r'
                    className={styles.label}
                  >
                    Smart meeting
                  </Typography>
                  <Typography
                    ariaLabel='SmartMeetingValue'
                    variant='body-md-regular'
                    className={styles.value}
                  >
                    {smartBookingEnabled ? 'ON' : 'OFF'}
                  </Typography>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className={classnames(styles.section, styles.secondSection)}>
        <div className={styles.knowledgeBaseRow}>
          <Typography variant='body-lg-medium' ariaLabel='AutoFollowUp'>
            Auto Follow-Up™
          </Typography>
          <Switch value={followUpEnabled} ariaLabel='AutoFollowUp' disabled size='middle' />
        </div>
        <div className={styles.sectionRows}>
          {followUpEnabled && (
            <>
              <div className={styles.sectionRow}>
                <Typography
                  ariaLabel='FollowUpWindow'
                  variant='text-input-field-r'
                  className={styles.label}
                >
                  Follow-up window
                </Typography>
                <Typography
                  ariaLabel='FollowUpWindowValue'
                  variant='body-md-regular'
                  className={styles.value}
                >
                  {followUpWindow} days
                </Typography>
              </div>
              <div className={styles.sectionRow}>
                <Typography
                  ariaLabel='Frequency'
                  variant='text-input-field-r'
                  className={styles.label}
                >
                  Frequency
                </Typography>
                <Typography
                  ariaLabel='FrequencyValue'
                  variant='body-md-regular'
                  className={styles.value}
                >
                  {capitalize(assertiveness)}
                </Typography>
              </div>
            </>
          )}
          <div className={styles.sectionRow}>
            <Typography
              ariaLabel='EndSession'
              variant='text-input-field-r'
              className={classnames(styles.label, { [styles.expandedLabel]: followUpEnabled })}
            >
              End session
            </Typography>
            <Typography
              ariaLabel='EndSessionValue'
              variant='body-md-regular'
              className={styles.value}
            >
              {endSessionText}
            </Typography>
          </div>
        </div>
      </div>
    </>
  )
})
